/**
 * Gin own-hand layout: melds stay in hand, grouped tighter with a box.
 *
 * Display packing (player planning first):
 *  1. Contiguous hand-order windows are locked as melds — even if worse deadwood.
 *     Drag cards into a run/set to force that grouping for planning.
 *  2. Only leftover (non-contiguous) cards are auto-packed for least deadwood,
 *     preferring sticky previous melds when options still tie.
 *
 * Run auto-sort is one-shot (caller tracks sorted keys). Layout preserves hand order.
 */

const RANK_ORDER = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const RANK_VAL = Object.fromEntries(RANK_ORDER.map((r, i) => [r, i + 1]));

export function rankValue(rank) {
  return RANK_VAL[rank] ?? 0;
}

export function sortCardsAscending(cards) {
  return [...(cards || [])].sort((a, b) => {
    const d = rankValue(a.rank) - rankValue(b.rank);
    if (d !== 0) return d;
    return String(a.suit || '').localeCompare(String(b.suit || ''));
  });
}

/** Stable key for a meld's card set (order-independent). */
export function meldCardKey(cards) {
  return (cards || []).map((c) => c.id).filter(Boolean).sort().join('|');
}

function isValidSet(cards) {
  if (!cards || cards.length < 3 || cards.length > 4) return false;
  if (!cards.every((c) => c.rank === cards[0].rank)) return false;
  const suits = new Set(cards.map((c) => c.suit));
  return suits.size === cards.length;
}

function isValidRun(cards) {
  if (!cards || cards.length < 3) return false;
  if (!cards.every((c) => c.suit === cards[0].suit)) return false;
  const sorted = sortCardsAscending(cards);
  for (let i = 1; i < sorted.length; i += 1) {
    if (rankValue(sorted[i].rank) !== rankValue(sorted[i - 1].rank) + 1) return false;
  }
  return true;
}

function contiguityScore(hand, cardIds) {
  const idx = cardIds
    .map((id) => hand.findIndex((c) => c.id === id))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  if (idx.length < 2) return 0;
  const span = idx[idx.length - 1] - idx[0] + 1;
  const gaps = span - idx.length;
  return (cardIds.length * 10) - (gaps * 4) - (span * 0.15);
}

/**
 * Preserve hand order of cards belonging to a meld (no forced ascending).
 */
function cardsInHandOrder(hand, cardIds) {
  const want = new Set(cardIds);
  return hand.filter((c) => want.has(c.id));
}

function withoutIds(hand, removeIds) {
  const rem = new Set(removeIds);
  return hand.filter((c) => !rem.has(c.id));
}

function cardDeadwoodPoints(card) {
  const r = card?.rank;
  if (r === 'A') return 1;
  if (r === 'J' || r === 'Q' || r === 'K') return 10;
  const n = Number(r);
  return Number.isFinite(n) ? n : 10;
}

function handDeadwoodPoints(cards) {
  return (cards || []).reduce((s, c) => s + cardDeadwoodPoints(c), 0);
}

/**
 * How well a candidate preserves prior melds.
 * Growing a run/set (3→6, 3 queens→4) beats keeping two short sticky fragments.
 */
function stickyScore(meld, previousMelds) {
  if (!previousMelds?.length) return 0;
  const key = meldCardKey(meld.cards);
  let best = 0;
  let containedPrev = 0;
  for (const prev of previousMelds) {
    if (!prev?.cards?.length) continue;
    const prevKey = meldCardKey(prev.cards);
    if (key && key === prevKey) {
      best = Math.max(best, 100);
      continue;
    }
    if (prev.type && meld.type && prev.type !== meld.type) continue;
    const prevIds = new Set(prev.cards.map((c) => c.id));
    const overlap = (meld.cards || []).filter((c) => prevIds.has(c.id)).length;
    if (overlap >= 2) {
      best = Math.max(best, 40 + overlap * 12);
    }
    // Full previous meld absorbed into a longer one (two runs of 3 → one of 6)
    if (overlap === prevIds.size && (meld.cards?.length || 0) > prevIds.size) {
      containedPrev += 1;
      best = Math.max(best, 130 + overlap * 5);
    }
  }
  if (containedPrev >= 2) {
    best = Math.max(best, 200); // strongly prefer the long merge
  }
  return best;
}

/**
 * Merge same-suit run fragments into maximal consecutive runs.
 * Fixes 3-4-5 + 6-7-8 (same suit) incorrectly boxed as two runs of 3.
 * Leaves gapped same-suit runs separate (A-2-3 and 5-6-7 stay two melds).
 */
function coalesceRunMelds(melds, hand) {
  if (!melds?.length) return melds || [];
  const runs = [];
  const rest = [];
  for (const m of melds) {
    if (m.type === 'run' && m.cards?.length) runs.push(m);
    else rest.push(m);
  }
  if (runs.length < 2) return melds;

  const bySuit = {};
  for (const m of runs) {
    const suit = m.suit || m.cards[0]?.suit || '?';
    if (!bySuit[suit]) bySuit[suit] = [];
    for (const c of m.cards) {
      bySuit[suit].push(c);
    }
  }

  const mergedRuns = [];
  for (const [suit, cards] of Object.entries(bySuit)) {
    const byRank = new Map();
    for (const c of cards) {
      if (c?.rank != null && !byRank.has(c.rank)) byRank.set(c.rank, c);
    }
    const sorted = sortCardsAscending([...byRank.values()]);
    let start = 0;
    while (start < sorted.length) {
      let end = start;
      while (
        end + 1 < sorted.length
        && rankValue(sorted[end + 1].rank) === rankValue(sorted[end].rank) + 1
      ) {
        end += 1;
      }
      const seg = sorted.slice(start, end + 1);
      if (seg.length >= 3) {
        mergedRuns.push({
          type: 'run',
          cards: cardsInHandOrder(hand, seg.map((c) => c.id)),
          suit,
        });
      }
      start = end + 1;
    }
  }

  return [...rest, ...mergedRuns];
}

/**
 * Lock non-overlapping contiguous melds from hand order (left → right, longest first).
 * This is player intent — even when worse for deadwood.
 */
function lockContiguousMelds(hand) {
  const locked = [];
  if (!hand?.length) return locked;
  let i = 0;
  while (i < hand.length) {
    let best = null;
    for (let len = Math.min(hand.length - i, 11); len >= 3; len -= 1) {
      const slice = hand.slice(i, i + len);
      if (isValidSet(slice)) {
        best = {
          type: 'set',
          cards: [...slice],
          rank: slice[0].rank,
          fromHandOrder: true,
        };
        break; // longest first
      }
      if (isValidRun(slice)) {
        best = {
          type: 'run',
          cards: [...slice],
          suit: slice[0].suit,
          fromHandOrder: true,
        };
        break;
      }
    }
    if (best) {
      locked.push(best);
      i += best.cards.length;
    } else {
      i += 1;
    }
  }
  return locked;
}

/**
 * Enumerate candidate melds among remaining cards (for auto least-deadwood pack).
 * Contiguous windows first, then maximal non-contiguous sets/runs.
 */
function enumerateCandidates(hand) {
  const candidates = [];
  if (!hand?.length) return candidates;
  const maxWindow = hand.length;

  for (let i = 0; i < hand.length; i += 1) {
    for (let len = maxWindow; len >= 3; len -= 1) {
      if (i + len > hand.length) continue;
      const slice = hand.slice(i, i + len);
      if (isValidSet(slice)) {
        candidates.push({
          type: 'set',
          cards: [...slice],
          rank: slice[0].rank,
          contiguous: true,
          contig: contiguityScore(hand, slice.map((c) => c.id)),
        });
      }
      if (isValidRun(slice)) {
        candidates.push({
          type: 'run',
          cards: [...slice],
          suit: slice[0].suit,
          contiguous: true,
          contig: contiguityScore(hand, slice.map((c) => c.id)),
        });
      }
    }
  }

  const byRank = {};
  const bySuit = {};
  for (const c of hand) {
    if (!byRank[c.rank]) byRank[c.rank] = [];
    byRank[c.rank].push(c);
    if (!bySuit[c.suit]) bySuit[c.suit] = [];
    bySuit[c.suit].push(c);
  }
  for (const cards of Object.values(byRank)) {
    if (cards.length >= 3) {
      const take = cardsInHandOrder(
        hand,
        cards.slice(0, Math.min(4, cards.length)).map((c) => c.id),
      );
      if (isValidSet(take)) {
        candidates.push({
          type: 'set',
          cards: take,
          rank: take[0].rank,
          contiguous: false,
          contig: contiguityScore(hand, take.map((c) => c.id)),
        });
      }
    }
  }
  for (const suitCards of Object.values(bySuit)) {
    const sorted = sortCardsAscending(suitCards);
    let start = 0;
    while (start < sorted.length) {
      let end = start;
      while (
        end + 1 < sorted.length
        && rankValue(sorted[end + 1].rank) === rankValue(sorted[end].rank) + 1
      ) {
        end += 1;
      }
      const runLen = end - start + 1;
      if (runLen >= 3) {
        for (let len = runLen; len >= 3; len -= 1) {
          for (let s = start; s + len - 1 <= end; s += 1) {
            const runSorted = sorted.slice(s, s + len);
            const run = cardsInHandOrder(hand, runSorted.map((c) => c.id));
            candidates.push({
              type: 'run',
              cards: run,
              suit: run[0]?.suit,
              contiguous: false,
              contig: contiguityScore(hand, run.map((c) => c.id)),
            });
          }
        }
      }
      start = end + 1;
    }
  }
  return candidates;
}

function betterAutoPack(a, b) {
  // 1) least deadwood
  if (a.dwPoints !== b.dwPoints) return a.dwPoints < b.dwPoints;
  // 2) more cards in melds
  if (a.covered !== b.covered) return a.covered > b.covered;
  // 3) fewer melds (one run of 6 beats two runs of 3)
  const aN = a.melds?.length ?? a.meldCount ?? 0;
  const bN = b.melds?.length ?? b.meldCount ?? 0;
  if (aN !== bN) return aN < bN;
  // 4) longer longest-meld (prefer 6+3 over 4+3+2-style packs)
  const aMax = a.maxMeldLen ?? 0;
  const bMax = b.maxMeldLen ?? 0;
  if (aMax !== bMax) return aMax > bMax;
  // 5) sticky previous, then contiguity
  if (a.sticky !== b.sticky) return a.sticky > b.sticky;
  if (a.contig !== b.contig) return a.contig > b.contig;
  return false;
}

function packStats(meldsSoFar) {
  let maxMeldLen = 0;
  for (const m of meldsSoFar || []) {
    maxMeldLen = Math.max(maxMeldLen, m.cards?.length || 0);
  }
  return {
    meldCount: (meldsSoFar || []).length,
    maxMeldLen,
  };
}

/**
 * Least-deadwood packing for cards the player has not grouped contiguously.
 */
function autoPackRemaining(hand, previousMelds = []) {
  if (!hand?.length || hand.length < 3) return [];

  let best = {
    melds: [],
    dwPoints: handDeadwoodPoints(hand),
    covered: 0,
    sticky: 0,
    contig: 0,
  };

  function search(remaining, meldsSoFar, stickySum, contigSum) {
    const dwPoints = handDeadwoodPoints(remaining);
    const covered = hand.length - remaining.length;
    const stats = packStats(meldsSoFar);
    const pack = {
      melds: meldsSoFar,
      dwPoints,
      covered,
      sticky: stickySum,
      contig: contigSum,
      ...stats,
    };
    if (betterAutoPack(pack, best)) {
      best = {
        ...pack,
        melds: meldsSoFar.map((m) => ({
          type: m.type,
          cards: m.cards,
          rank: m.rank,
          suit: m.suit,
        })),
      };
    }
    if (remaining.length < 3) return;
    // Keep searching when dw=0 — a single longer run may still beat two short ones

    const next = enumerateCandidates(remaining);
    // Longer melds first so a 6-run is tried before 3-runs
    next.sort((a, b) => {
      if (b.cards.length !== a.cards.length) return b.cards.length - a.cards.length;
      const sa = stickyScore(a, previousMelds) + (a.contiguous ? 5 : 0);
      const sb = stickyScore(b, previousMelds) + (b.contiguous ? 5 : 0);
      if (sb !== sa) return sb - sa;
      return (b.contig || 0) - (a.contig || 0);
    });

    const seen = new Set();
    let tried = 0;
    for (const meld of next) {
      const key = meldCardKey(meld.cards);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      tried += 1;
      if (tried > 32) break;
      const rest = withoutIds(remaining, meld.cards.map((c) => c.id));
      search(
        rest,
        [...meldsSoFar, meld],
        stickySum + stickyScore(meld, previousMelds),
        contigSum + (meld.contig || 0),
      );
      // Only stop early once we have a perfect pack that uses the longest possible first meld
      if (
        best.dwPoints === 0
        && best.covered === hand.length
        && best.maxMeldLen >= (meld.cards?.length || 0)
        && tried > 12
      ) {
        return;
      }
    }
  }

  search(hand, [], 0, 0);
  return coalesceRunMelds(best.melds, hand);
}

/**
 * Build display melds:
 *  - Contiguous hand groups = player choice (may be inefficient for planning)
 *  - Everything else auto-packed for least deadwood (+ sticky when ties)
 */
export function preferentialMeldsFromOrder(orderedHand, options = {}) {
  const hand = orderedHand || [];
  const previousMelds = options.previousMelds || [];
  if (hand.length < 3) return [];

  const locked = lockContiguousMelds(hand);
  const lockedIds = new Set();
  for (const m of locked) {
    for (const c of m.cards || []) {
      if (c?.id != null) lockedIds.add(c.id);
    }
  }
  const remaining = hand.filter((c) => !lockedIds.has(c.id));

  // Sticky preference only among auto leftovers (don't fight player-locked groups)
  const auto = autoPackRemaining(remaining, previousMelds);

  const combined = [
    ...locked.map((m) => ({
      type: m.type,
      cards: m.cards,
      rank: m.rank,
      suit: m.suit,
    })),
    ...auto,
  ];
  // Always coalesce run fragments (e.g. 3-4-5 + 6-7-8 → one 3–8 run)
  return coalesceRunMelds(combined, hand);
}

/**
 * One-time ascending sort for runs whose card-set key is not yet in `alreadySortedKeys`.
 * Returns { order, newKeys } — newKeys should be merged into the caller's sorted set.
 */
export function applyOneTimeRunSort(handOrder, orderedCards, melds, alreadySortedKeys = new Set()) {
  if (!handOrder?.length || !melds?.length) {
    return { order: handOrder, newKeys: [] };
  }
  const byId = new Map((orderedCards || []).map((c) => [c.id, c]));
  let order = [...handOrder];
  const newKeys = [];

  for (const m of melds) {
    if (m.type !== 'run' || !m.cards?.length) continue;
    const key = meldCardKey(m.cards);
    if (!key || alreadySortedKeys.has(key)) continue;

    const ids = new Set(m.cards.map((c) => c.id));
    const indices = [];
    order.forEach((id, i) => {
      if (ids.has(id)) indices.push(i);
    });
    if (indices.length < 2) continue;
    const start = Math.min(...indices);
    const runCards = sortCardsAscending(
      m.cards.map((c) => byId.get(c.id) || c).filter(Boolean),
    );
    order = order.filter((id) => !ids.has(id));
    const insertAt = Math.min(start, order.length);
    order.splice(insertAt, 0, ...runCards.map((c) => c.id));
    newKeys.push(key);
  }
  return { order, newKeys };
}

/** @deprecated use applyOneTimeRunSort — kept for any leftover imports */
export function applyRunSortToOrder(handOrder, orderedCards, melds) {
  const { order } = applyOneTimeRunSort(handOrder, orderedCards, melds, new Set());
  return order;
}

export function layoutGinHandGroups(orderedHand, melds, {
  cardW,
  cardH,
  availWidth = 0,
}) {
  const n = orderedHand?.length || 0;
  if (n === 0) {
    return {
      positions: new Map(),
      groupBoxes: [],
      boxW: availWidth || cardW,
      boxH: cardH + 16,
      totalSpan: 0,
      startOffset: 0,
      groups: [],
    };
  }

  const meldStep = Math.max(12, Math.round(cardW * 0.34));
  const deadStep = Math.max(16, Math.round(cardW * 0.52));
  const groupGap = Math.max(12, Math.round(cardW * 0.2));
  const boxPadX = 5;
  const boxPadY = 5;

  // Preserve player order inside melds (no forced run re-sort here)
  const idToMeld = new Map();
  (melds || []).forEach((m, mi) => {
    for (const c of m.cards || []) {
      if (c?.id != null) idToMeld.set(c.id, { mi, type: m.type || 'set' });
    }
  });

  const meldFirst = new Map();
  orderedHand.forEach((c, i) => {
    const info = idToMeld.get(c.id);
    if (info && !meldFirst.has(info.mi)) meldFirst.set(info.mi, i);
  });
  const meldOrder = [...meldFirst.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([mi]) => mi);

  const groups = [];
  for (const mi of meldOrder) {
    const m = melds[mi];
    const cards = orderedHand.filter((c) => idToMeld.get(c.id)?.mi === mi);
    if (cards.length) {
      groups.push({
        key: `meld-${mi}`,
        type: m.type || 'set',
        isMeld: true,
        cards,
      });
    }
  }
  const deadCards = orderedHand.filter((c) => !idToMeld.has(c.id));
  if (deadCards.length) {
    groups.push({
      key: 'deadwood',
      type: 'deadwood',
      isMeld: false,
      cards: deadCards,
    });
  }
  if (!groups.length) {
    groups.push({
      key: 'all',
      type: 'deadwood',
      isMeld: false,
      cards: orderedHand,
    });
  }

  let x = 0;
  const positions = new Map();
  const groupBoxes = [];

  for (let gi = 0; gi < groups.length; gi += 1) {
    const g = groups[gi];
    const step = g.isMeld ? meldStep : deadStep;
    const gStart = x;
    g.cards.forEach((c, i) => {
      positions.set(c.id, gStart + i * step);
    });
    const gWidth = g.cards.length <= 1
      ? cardW
      : cardW + (g.cards.length - 1) * step;
    if (g.isMeld) {
      groupBoxes.push({
        key: g.key,
        type: g.type,
        left: gStart - boxPadX,
        width: gWidth + boxPadX * 2,
        top: -boxPadY,
        height: cardH + boxPadY * 2,
      });
    }
    x = gStart + gWidth + (gi < groups.length - 1 ? groupGap : 0);
  }

  const totalSpan = x;
  const boxW = Math.max(availWidth || 0, totalSpan);
  const startOffset = Math.max(0, (boxW - totalSpan) / 2);

  const shifted = new Map();
  for (const [id, left] of positions) {
    shifted.set(id, left + startOffset);
  }
  const shiftedBoxes = groupBoxes.map((b) => ({
    ...b,
    left: b.left + startOffset,
  }));

  return {
    positions: shifted,
    groupBoxes: shiftedBoxes,
    boxW,
    boxH: cardH + boxPadY * 2 + 6,
    totalSpan,
    startOffset,
    groups,
  };
}

export function layoutOppFacedown(count, cardW, cardH) {
  const step = Math.max(10, Math.round(cardW * 0.38));
  const w = count <= 0 ? 0 : cardW + Math.max(0, count - 1) * step;
  return { step, width: w, height: cardH + 4 };
}

export function layoutGinRevealHand(cards, melds, cardW, cardH) {
  return layoutGinHandGroups(cards || [], melds || [], {
    cardW,
    cardH,
    availWidth: 0,
  });
}
